import React, { useMemo } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';

import { LabelTemplateSlug, WarehouseLabelDepartment } from '../model';

interface DepartmentMultiSelectProps {
  departments: WarehouseLabelDepartment[];
  slug: LabelTemplateSlug;
  codeOwner: Record<string, LabelTemplateSlug>;
  templateNames: Record<string, string>;
  value: string[];
  onChange: (codes: string[]) => void;
  disabled?: boolean;
}

const DepartmentMultiSelect: React.FC<DepartmentMultiSelectProps> = ({
  departments,
  slug,
  codeOwner,
  templateNames,
  value,
  onChange,
  disabled,
}) => {
  const byCode = useMemo(() => new Map(departments.map((d) => [d.code, d])), [departments]);

  // An assignment can outlive its department: the Servman sync prunes the mirror, and the
  // admin still has to be able to see the row and clear it.
  const selected = useMemo(
    () => value.map((code) => byCode.get(code) ?? { code, name: code, slug }),
    [value, byCode, slug],
  );

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      limitTags={8}
      disabled={disabled}
      options={departments}
      value={selected}
      onChange={(_event, next) => onChange(next.map((d) => d.code))}
      getOptionLabel={(option) => option.name || option.code}
      isOptionEqualToValue={(option, selectedOption) => option.code === selectedOption.code}
      // Admins read the description, but the code is what Servman shows them elsewhere, so
      // the search matches either.
      filterOptions={(options, { inputValue }) => {
        const query = inputValue.trim().toLowerCase();
        if (!query) return options;
        return options.filter((option) => `${option.name} ${option.code}`.toLowerCase().includes(query));
      }}
      renderOption={(props, option) => {
        const owner = codeOwner[option.code];
        return (
          <Box component="li" {...props} key={option.code}>
            <Box>
              <Typography variant="body2">{option.name || option.code}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.code}
                {/* A department prints one template, so picking it here is a move, not a copy. */}
                {owner && owner !== slug && ` — currently in ${templateNames[owner] ?? owner}`}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} label="Departments" placeholder="Search by description or code" size="small" />
      )}
      sx={{ mb: 2 }}
    />
  );
};

export default DepartmentMultiSelect;
